import {
  ActionFunctionArgs,
  json,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from "@remix-run/node";
import {
  useFetcher,
  useLoaderData,
  useNavigation,
  useParams,
  useRouteLoaderData,
} from "@remix-run/react";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { parseFormData } from "remix-hook-form";
import invariant from "tiny-invariant";

import ContactSales from "~/components/molecules/ContactSales";
import ContentsNavigator from "~/components/organisms/ContentsNavigator";
import { IContentSection } from "~/components/organisms/ContentsNavigator/types";
import FloatingShowflatButton from "~/components/organisms/FloatingShowflatButton";
import MastheadGallery from "~/components/organisms/MastheadGallery";
import { PriceListModal } from "~/components/organisms/PriceListModal";
import ProjectDetailContactSale from "~/components/organisms/ProjectDetailContactSaleModal/ProjectDetailContactSaleModal";
import ProjectDetailModal from "~/components/organisms/ProjectDetailModal/ProjectDetailModal";
import RelatedPropertiesModal from "~/components/organisms/RelatedPropertiesModal/RelatedPropertiesModal";
import SectionCuratedProperties from "~/components/sections/SectionCuratedProperties";
import SectionProjectAvailableUnits from "~/components/sections/SectionProjectAvailableUnits";
import SectionProjectDetails from "~/components/sections/SectionProjectDetails";
import SectionProjectFloorPlans from "~/components/sections/SectionProjectFloorPlans";
import SectionProjectInfo from "~/components/sections/SectionProjectInfo";
import SectionProjectLocationMap from "~/components/sections/SectionProjectLocationMap";
import SectionProjectMasonryGallery from "~/components/sections/SectionProjectMasonryGallery";
import SectionProjectSaleTransaction from "~/components/sections/SectionProjectSaleTransaction";
import SectionProjectSitePlan from "~/components/sections/SectionProjectSitePlan";
import SectionProjectUnitMix from "~/components/sections/SectionProjectUnitMix";
import SectionProjectVirtualTour from "~/components/sections/SectionProjectVirtualTour";
import SectionScrollToEnquiry from "~/components/sections/SectionScrollToEnquiry";
import { IFloorPlanItem } from "~/entities/floorPlan";
import { IIpTrackingDTO } from "~/entities/tracking";
import { IUnitPrice } from "~/entities/unitPrice";
import { useIsClient } from "~/hooks/use-is-client";
import useMenuScroll from "~/hooks/use-menu-scroll";
import i18nServer from "~/locales/i18n.server";
import { getUnitTypes } from "~/services/commonService";
import {
  submitContactForm,
  TContactFormReq,
} from "~/services/contactSaleService";
import {
  getCuratedProjects,
  getFloorPlanOptionsByProject,
  getFloorPlans,
  getPaginatedFloorPlansByProjectAndUnitTypes,
  getProject,
  getSaleTransactionOptionsByProject,
  getUnitPriceList,
  getUnitTransactions,
} from "~/services/projectService";
import { trackingUserByProject } from "~/services/trackingService";
import { commitSession, getSession } from "~/session";
import useAppState from "~/stores";
import {
  IPaginateResponseData,
  ITimelineEvent,
  ITrackingTimeline,
} from "~/types";
import { TMetadata } from "~/types/metadata";
import { TProjectLoaderData } from "~/types/projectLoaderData";
import { IRouteLoaderData } from "~/types/routeLoaderData";
import { IUser } from "~/types/user";
import projectSectionConfig from "~/utilities/config/projectSectionConfig";
import {
  COUNTRY_PHONE_NUMBER_DIALCODE,
  FORMAT_FULLDAY,
  LIMIT,
  OPTIONS_CONTACT_SALE_INTERESTED,
  PAGE,
} from "~/utilities/constants/common";
import { projectSections } from "~/utilities/constants/projectSections";
import { EApiParam } from "~/utilities/enums/ApiParam";
import { Section } from "~/utilities/enums/ProjectSections";
import { ESort } from "~/utilities/enums/SearchParams";
import { Slug } from "~/utilities/enums/Slug";
import { EUnitColumn } from "~/utilities/enums/Unit";
import findFirstJpegSource from "~/utilities/helpers/findFirstImage";
import formatCurrencyRange from "~/utilities/helpers/formatCurrencyPSFRange";
import { getClientInformation } from "~/utilities/helpers/getClientInformation";
import { getRawText } from "~/utilities/helpers/getRawText";
import isValidJson from "~/utilities/helpers/isValidJson";
import { getMetadata } from "~/utilities/helpers/metadata";
import { extractDomainFromRequest } from "~/utilities/helpers/requestHelper";
import { TContactSaleFormData } from "~/utilities/schema/contact-sale";

// export type TProjectLoaderData = {
//   siteUrl: string;
//   projectMenuItems: IContentSection[];
//   project: IProject;
//   floorPlansPagination: IPaginateResponseData<IFloorPlan>;
//   unitTransactions: IPaginateResponseData<IUnitTransaction>;
//   curatedProjects: IProject[];
//   virtualTours: IVirtualTour[];
//   floorPlansModalPagination: IPaginateResponseData<IFloorPlan>;
//   filterOptions: {
//     unitTypes: IOption[];
//   };
//   saleTransactionFilterOptions: ISaleTransactionFilterOption[];
//   floorPlanFilterOptions: IFloorPlanFilterOption[];
// };

const LOADER_ACTIONS = {
  TRACKING_USER: "trackingUserData",
};

// TODO: Handle get all projects
// export const handle: SEOHandle = {
//   getSitemapEntries: async (request) => {
//     const domain = extractDomainFromRequest(request);
//     const resGetProjects = await getProjects({ domain });
//     const projects = resGetProjects?.items;
//     return projects?.map((project) => {
//       return { route: `${Slug.PROJECTS}${project.slug}`, priority: 0.7 };
//     });
//   },
// };

export const meta: MetaFunction<typeof loader> = (args) => {
  const { data } = args;
  const { siteUrl, project } = { ...data };

  if (isEmpty(project)) return [];

  const SEOTitle = isEmpty(project?.seoTitle)
    ? project?.name
    : project?.seoTitle;
  const SEODescription = isEmpty(project?.seoDescription)
    ? project?.description
    : project?.seoDescription;
  const SEOImage =
    ENV?.IMAGE_DOMAIN_URL +
    (isEmpty(project?.seoImage)
      ? findFirstJpegSource([project?.photo])?.urls[0]?.url
      : findFirstJpegSource([project?.seoImage])?.urls[0]?.url);

  const metaArgs: Partial<TMetadata> = {
    title: SEOTitle ?? "",
    description: getRawText(SEODescription),
    siteUrl,
    image: SEOImage ?? "",
    additionalMeta: [
      { name: "og:type", content: "website" },
      { name: "og:site_name", content: SEOTitle ?? "" },
    ],
  };

  // Filter out undefined properties
  const filteredMetaArgs = Object.fromEntries(
    Object.entries(metaArgs).filter(([_, value]) => value !== undefined)
  );

  return getMetadata(filteredMetaArgs as TMetadata);
};

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const t = await i18nServer.getFixedT(request);

  const clientInformation = getClientInformation(request);

  const url = new URL(request.url);
  const domain = extractDomainFromRequest(request);
  const siteUrl = url.protocol + "//" + url.host + url.pathname;

  const project = await getProject(domain, params.slug as string);

  if (!project || !project?.id) {
    throw new Response("We can’t find the project that you’re looking for", {
      status: 404,
      statusText: "Project not found",
    });
  }

  const tz = url.searchParams.get("tz");
  const triggerAction = url.searchParams.get("trigger");

  if (
    project.id &&
    clientInformation?.ip &&
    !!tz &&
    triggerAction === LOADER_ACTIONS.TRACKING_USER
  ) {
    const [region, city] = tz.split("/");
    const trackingUserData: IIpTrackingDTO = {
      ip: clientInformation.ip || "Unknown",
      region: region,
      projectId: project.id,
      projectLink: siteUrl,
      projectName: project.name,
      browser: clientInformation.browser,
      country: clientInformation?.country,
      countryCode: clientInformation?.countryCode,
    };
    await trackingUserByProject(trackingUserData, domain);
  }

  const saleTransactionFilterOptions = await getSaleTransactionOptionsByProject(
    domain,
    params.slug as string
  );

  const floorPlanFilterOptions = await getFloorPlanOptionsByProject(
    domain,
    params.slug as string
  );

  const unitTypeIds = url.searchParams.get(EApiParam.UNITTYPEIDS);
  const page = url.searchParams.get(EApiParam.PAGE) || PAGE;
  const limit = url.searchParams.get(EApiParam.LIMIT) || LIMIT;

  const floorPlansPagination = unitTypeIds
    ? await getPaginatedFloorPlansByProjectAndUnitTypes(
        domain,
        params.slug as string,
        {
          [EApiParam.UNITTYPEIDS]: url.searchParams.get(
            EApiParam.UNITTYPEIDS
          ) as string,
          [EApiParam.PAGE]: `${page}`,
          [EApiParam.LIMIT]: `${limit}`,
        }
      )
    : await getFloorPlans(domain, params.slug as string, {
        page: Number(page),
        limit: Number(limit),
      });

  const pricePage = url.searchParams.get(EApiParam.PRICE_PAGE) || PAGE;
  const priceLimit = url.searchParams.get(EApiParam.PRICE_LIMIT) || LIMIT;

  const floorPlansModalPagination = await getFloorPlans(
    domain,
    (params?.slug || "") as string,
    {
      page: Number(pricePage),
      limit: Number(priceLimit),
    }
  );

  const fromDate =
    url.searchParams.get(EApiParam.START_DATE) ??
    (saleTransactionFilterOptions?.[0]?.fromDate || "");

  const toDate =
    url.searchParams.get(EApiParam.END_DATE) ??
    (saleTransactionFilterOptions?.[0]?.toDate || "");

  const unitTransactions = await getUnitTransactions(domain, {
    [EApiParam.PROJECT_SLUG]: params?.slug || "",
    [EApiParam.START_DATE]: fromDate,
    [EApiParam.END_DATE]: toDate,
    [EApiParam.PAGE]: `${page}`,
    [EApiParam.LIMIT]: `${limit}`,
    [EApiParam.SORT_BY]: `timestamp`,
    [EApiParam.SORT]: ESort.ASC,
  });

  let unitPricesPagination: IPaginateResponseData<IUnitPrice> & {
    [EApiParam.SORT]?: ESort;
    [EApiParam.SORT_BY]?: EUnitColumn;
  } = {} as IPaginateResponseData<IUnitPrice> & {
    [EApiParam.SORT]?: ESort;
    [EApiParam.SORT_BY]?: EUnitColumn;
  };
  const unitPriceSort = ESort.ASC;
  const unitPriceSortBy = EUnitColumn.UNIT_NAME;

  if (params?.slug) {
    unitPricesPagination = await getUnitPriceList(domain, params.slug, {
      [EApiParam.PAGE]: `${page}`,
      [EApiParam.LIMIT]: `${LIMIT}`,
      [EApiParam.SORT_BY]: unitPriceSortBy,
      [EApiParam.SORT]: unitPriceSort,
    });
  }

  unitPricesPagination = {
    ...unitPricesPagination,
    [EApiParam.SORT_BY]: unitPriceSortBy,
    [EApiParam.SORT]: unitPriceSort,
  };

  const curatedProjects = await getCuratedProjects(domain, params?.slug || "");

  const projectMenuItems = [...projectSections];

  const virtualTours = project?.virtualTours || [];

  // get all filter api option
  const resUnitTypes = await getUnitTypes(domain);

  if (project?.photoId && project?.photo) {
    project?.medias.unshift(project?.photo);
  }

  const loaderData: TProjectLoaderData = {
    siteUrl,
    projectMenuItems,
    project,
    unitPricesPagination,
    floorPlansPagination,
    unitTransactions,
    curatedProjects,
    virtualTours,
    floorPlansModalPagination,
    filterOptions: {
      unitTypes: resUnitTypes?.map((unit) => ({
        id: unit.slug,
        name: unit.title,
      })),
    },
    saleTransactionFilterOptions: Array.isArray(saleTransactionFilterOptions)
      ? saleTransactionFilterOptions
      : [],
    floorPlanFilterOptions: Array.isArray(floorPlanFilterOptions)
      ? floorPlanFilterOptions?.sort((a, b) => a.title.localeCompare(b.title))
      : [],
  };
  return json(loaderData);
};

export async function action({ request, params }: ActionFunctionArgs) {
  const t = await i18nServer.getFixedT(request);
  invariant(params.slug, t("error.slug.missing"));
  const domain = extractDomainFromRequest(request);
  const formData = await parseFormData<TContactSaleFormData>(request, false);

  let trackingTimeline =
    formData?.tracking &&
    isValidJson(formData?.tracking) &&
    (JSON.parse(formData?.tracking) as ITrackingTimeline);

  trackingTimeline = trackingTimeline
    ? { ...trackingTimeline, endTime: new Date() }
    : undefined;

  const tempData: TContactFormReq = {
    projectSlug: params.slug || "",
    interestedIn:
      JSON.stringify(
        formData?.interested?.map((i) => {
          const findValues = OPTIONS_CONTACT_SALE_INTERESTED.find(
            (val) => val.id === i
          )?.name;
          return findValues || i;
        })
      ) || "",
    phone: `${COUNTRY_PHONE_NUMBER_DIALCODE}${formData?.phone}`,
    name: formData?.name,
    email: formData?.email,
    appointment: dayjs(
      `${formData?.appointmentDate} ${formData?.appointmentTime}`
    ).format(FORMAT_FULLDAY),
    unitTypes: JSON.stringify(formData?.unitTypes) || "",
    tracking: JSON.stringify(trackingTimeline) || "",
  };
  const clientInformation = getClientInformation(request);

  const response = await submitContactForm(
    {
      data: tempData,
      userAgent: clientInformation?.userAgent || null,
      ip: clientInformation?.ip || null,
    },
    domain
  );
  if (response) {
    const session = await getSession(request.headers.get("Cookie"));
    session.flash("message", "enquiryReceived");
    const cookie = await commitSession(session);
    return redirect(`${Slug.ENQUIRY_RECEIVED}`, {
      headers: {
        "Set-Cookie": cookie,
      },
    });
  }
  return null;
}

export default function Index() {
  const {
    projectMenuItems,
    project,
    floorPlansPagination,
    unitTransactions,
    curatedProjects,
    virtualTours,
    floorPlansModalPagination,
    saleTransactionFilterOptions,
    floorPlanFilterOptions,
  } = useLoaderData() as unknown as TProjectLoaderData;

  const fetcher = useFetcher<typeof loader>();
  const { setAlert } = useAppState((state) => state);

  const onLoadMore = () => {
    if (
      fetcher.state === "loading" ||
      (typeof fetcher?.data?.floorPlansModalPagination?.currentPage ===
        "number" &&
        typeof fetcher?.data?.floorPlansModalPagination?.totalPage ===
          "number" &&
        fetcher?.data?.floorPlansModalPagination?.currentPage ===
          fetcher?.data?.floorPlansModalPagination?.totalPage) ||
      (typeof floorPlansModalPagination?.currentPage === "number" &&
        typeof floorPlansModalPagination?.totalPage === "number" &&
        floorPlansModalPagination?.currentPage ===
          floorPlansModalPagination?.totalPage)
    )
      return;

    const page = fetcher?.data?.floorPlansModalPagination?.currentPage
      ? fetcher?.data?.floorPlansModalPagination?.currentPage + 1
      : floorPlansModalPagination?.currentPage + 1;
    const limit = fetcher?.data?.floorPlansModalPagination?.limit
      ? fetcher?.data?.floorPlansModalPagination?.limit
      : floorPlansModalPagination?.limit;

    const query = `?${EApiParam.PRICE_PAGE}=${page}&${EApiParam.PRICE_LIMIT}=${limit}`;
    fetcher.load(query);
  };

  const navigation = useNavigation();

  const { t } = useTranslation();

  const {
    addRelatedProject,
    addTrackingData,
    appointmentOpen,
    setAppointmentOpen,
  } = useAppState((state) => state);
  const [relatedProjectId, setRelatedProjectId] = useState<string>("");

  const [currentSection, setCurrentSection] = useState<IContentSection>();
  const [hasScrolledToHash, setHasScrolledToHash] = useState(false);

  const [modalState, setModalState] = useState<
    | "unitType"
    | "contact"
    | "projectCurated"
    | "priceList"
    | "masthead_contact"
    | null
  >();

  const onCloseModalState = () => {
    setModalState((prev) =>
      prev === "masthead_contact" ? "projectCurated" : null
    );
  };

  const params = useParams<{ slug: string }>();
  const currentSlug = params?.slug;

  const rootData = useRouteLoaderData("root") as IRouteLoaderData;
  let user: IUser = rootData?.userConfig;

  const config = rootData?.config;

  const sectionRefs = useRef<HTMLDivElement[]>([]);
  const { activeIndex, scrollToSection, menuRef } = useMenuScroll({
    sections: sectionRefs.current,
  });

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, projectMenuItems.length);
  }, [projectMenuItems.length]);

  if (!user?.photo && !!config?.agencyPhoto)
    user = { ...user, photo: config.agencyPhoto };
  if (!user?.phone && !!config?.phoneNumber)
    user = { ...user, phone: config.phoneNumber };

  useEffect(() => {
    if (project) {
      const timelineEvent: ITimelineEvent = {
        visitedAt: new Date(),
        projectId: project.id,
        projectName: project.name,
      };

      // if (navigation?.state === "submitting") {
      //   timelineEvent.submittedAt = new Date();
      //   setAlert(true, "Submitted");
      // }

      addTrackingData(timelineEvent, navigation?.state === "submitting");
    }
  }, [addTrackingData, navigation?.state, project]);

  // useEffect(() => {
  //   // Note: default open modal suggest

  //   if (project?.unitTypes?.length > 0) {
  //     setModalState("unitType");
  //   } else {
  //     setModalState("projectCurated");
  //   }
  // }, [currentSlug, project?.unitTypes?.length]);

  const handleClickShowflatButton = () => {
    setAppointmentOpen(true);
    setModalState("projectCurated");
  };

  const floorPlanItems: IFloorPlanItem[] = useMemo(() => {
    return (
      (
        fetcher?.data?.floorPlansModalPagination?.items ||
        floorPlansModalPagination?.items ||
        []
      )?.map((item) => ({
        type: item?.unitType?.title || "",
        floorPlanName: item?.name || "",
        totalUnit: item?.totalUnits || 0,
        sold: (item?.totalUnits || 0) - item?.availableUnits || 0,
        available: item?.availableUnits || 0,
        size: item?.area || 0,
        price: formatCurrencyRange(item?.minPrice || 0, item?.maxPrice || 0),
        psf: formatCurrencyRange(item?.minPSF || 0, item?.maxPSF || 0),
      })) || []
    );
  }, [
    fetcher?.data?.floorPlansModalPagination?.items,
    floorPlansModalPagination?.items,
  ]);

  const isClient = useIsClient();

  const projectMenuItemsConverted = useMemo(() => {
    if (!isClient) return projectMenuItems;

    return projectMenuItems
      ?.filter((item) => !!document.getElementById(item.targetId))
      ?.map((item) => {
        const isExist = !!document.getElementById(item.targetId);
        return {
          ...item,
          disabled: !isExist,
        };
      });
  }, [isClient, projectMenuItems]);

  const defaultSection = projectMenuItemsConverted.find(
    (item) => !item.disabled
  );

  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const query = `?trigger=${LOADER_ACTIONS.TRACKING_USER}&tz=${timezone}`;
    fetcher.load(query);
  }, [currentSlug]);
  if (!project) return <></>;
  return (
    <main className="bg-backgroundPageProject">
      <div className="flex min-h-screen w-full flex-col">
        {project?.medias?.length > 0 && (
          <MastheadGallery
            project={project}
            onShowPriceList={() => setModalState("priceList")}
            onShowFlat={() => {
              setModalState("masthead_contact");
            }}
          />
        )}
        {defaultSection && (
          <ContentsNavigator
            ref={menuRef}
            sections={projectMenuItemsConverted}
            defaultSection={defaultSection}
            onClick={() => {}}
          />
        )}

        <SectionProjectInfo
          ref={(el) => (sectionRefs.current[0] = el!)}
          id={Section.DESCRIPTION}
          project={project}
          className="container-content"
        />
        <SectionProjectDetails
          ref={(el) => (sectionRefs.current[1] = el!)}
          id={Section.PROJECT_DETAILS}
          title={projectSectionConfig[Section.PROJECT_DETAILS].title}
          project={project}
          className="container-content"
        />
        {virtualTours?.length > 0 && (
          <SectionProjectVirtualTour
            ref={(el) => (sectionRefs.current[2] = el!)}
            id={Section.VIRTUAL_TOUR}
            title={projectSectionConfig[Section.VIRTUAL_TOUR].title}
            description={projectSectionConfig[Section.VIRTUAL_TOUR].description}
            virtualTours={virtualTours}
            className="container-content"
            unitTypeOptions={project?.unitTypes?.map((unitType) => ({
              id: unitType?.id,
              name: unitType?.title,
            }))}
          />
        )}
        {(project?.sitePlanImage || project?.facilityHtml) && (
          <SectionProjectSitePlan
            ref={(el) => (sectionRefs.current[3] = el!)}
            id={Section.SITE_PLAN}
            title={projectSectionConfig[Section.SITE_PLAN].title}
            description={projectSectionConfig[Section.SITE_PLAN].description}
            project={project}
            className="container-content"
          />
        )}

        {(project?.amenityHtml || project?.googleMapUrl) && (
          <SectionProjectLocationMap
            id={Section.LOCATION_MAP}
            title={projectSectionConfig[Section.LOCATION_MAP].title}
            description={projectSectionConfig[Section.LOCATION_MAP].description}
            project={project}
            className="container-content"
          />
        )}
        {project?.medias?.length > 0 && (
          <SectionProjectMasonryGallery
            id={Section.GALLERY}
            title={projectSectionConfig[Section.GALLERY].title}
            project={project}
            className="container-content"
          />
        )}
        {project?.unitTypes?.length > 0 && (
          <SectionProjectUnitMix
            id={Section.UNIT_MIX}
            title={projectSectionConfig[Section.UNIT_MIX].title}
            description={projectSectionConfig[Section.UNIT_MIX].description}
            project={project}
            className="container-content"
          />
        )}
        <ContactSales
          id={Section.CONTACT_SALES}
          title={t("contact_sales.heading")}
          user={user}
          className="container-content"
          description={t("contact_sales.description")}
        />
        {project?.unitTypes?.length > 0 && (
          <SectionProjectAvailableUnits
            id={Section.AVAILABLE_UNITS}
            title={projectSectionConfig[Section.AVAILABLE_UNITS].title}
            description={
              projectSectionConfig[Section.AVAILABLE_UNITS].description
            }
            project={project}
            className="container-content"
          />
        )}
        {floorPlansPagination?.total > 0 && (
          <SectionProjectFloorPlans
            id={Section.FLOOR_PLANS}
            title={projectSectionConfig[Section.FLOOR_PLANS].title}
            description={projectSectionConfig[Section.FLOOR_PLANS].description}
            floorPlansPagination={floorPlansPagination}
            project={project}
            className="container-content"
            unitTypeFilterOptions={floorPlanFilterOptions}
          />
        )}
        {unitTransactions?.items?.length > 0 && (
          <SectionProjectSaleTransaction
            id={Section.SALES_TRANSACTION}
            title={projectSectionConfig[Section.SALES_TRANSACTION].title}
            description={
              projectSectionConfig[Section.SALES_TRANSACTION].description
            }
            unitTransactions={unitTransactions}
            saleTransactionFilterOptions={saleTransactionFilterOptions}
            className="container-content"
          />
        )}
        <SectionScrollToEnquiry
          id={Section.SCROLL_TO_ENQUIRY}
          title={projectSectionConfig[Section.SCROLL_TO_ENQUIRY].title}
          description={
            projectSectionConfig[Section.SCROLL_TO_ENQUIRY].description
          }
          className="container-content my-6 lg:my-0"
          projectName={project.name}
        />

        {curatedProjects?.length > 0 && (
          <SectionCuratedProperties
            id={Section.CURATED_PROPERTIES}
            title="project.curated_properties"
            className="mt-6"
            projects={curatedProjects.slice(0, 8)}
            handleOnSelectProject={onCloseModalState}
          />
        )}
      </div>
      <RelatedPropertiesModal
        setRelatedProjectId={setRelatedProjectId}
        relatedProjects={curatedProjects.slice(0, 5)}
        open={modalState === "projectCurated"}
        onClose={onCloseModalState}
      />
      <ProjectDetailModal
        project={project}
        open={modalState === "unitType"}
        onClose={(ids) => {
          if (curatedProjects.length > 0)
            return setModalState("projectCurated");
          return onCloseModalState();
        }}
        onShowFlat={() => {
          setModalState("contact");
        }}
        onShowPriceList={() => setModalState("priceList")}
      />
      <ProjectDetailContactSale
        project={project}
        open={
          modalState === "contact" ||
          modalState === "masthead_contact" ||
          appointmentOpen
        }
        onClose={() => {
          if (curatedProjects.length > 0 && appointmentOpen)
            // Only open project curated modal after this one close if there are curated projects and click on showflat button header
            return setModalState("projectCurated");
          return onCloseModalState();
        }}
      />
      <PriceListModal
        items={floorPlanItems}
        page={fetcher?.data?.floorPlansModalPagination?.currentPage || 1}
        onLoadMore={onLoadMore}
        isLoading={fetcher?.state === "loading"}
        open={modalState === "priceList"}
        onClose={onCloseModalState}
      />
      <FloatingShowflatButton onClick={handleClickShowflatButton} />
    </main>
  );
}
